import { useSelector } from "react-redux";
import useSWR from "swr";
import { LedgerApi } from "../apis";
import {
  LEDGER_GROUP_ORDER,
  LEDGER_SECTIONS,
  type LedgerGroupKey,
  type LedgerSection,
} from "../constants/ledger-sections";
import { IAuth } from "../interfaces/IAuth";

export type VisibleLedgerSection = LedgerSection & { disabled: boolean };
export type VisibleLedgerGroup = { key: LedgerGroupKey; sections: VisibleLedgerSection[] };

/**
 * Same hiding rules as the sidebar: supplierOnly is hidden from non-suppliers,
 * ledgerOnly while the ledger is off (and while the mode is still loading).
 * A section the business lacks the menu permission for stays listed but
 * `disabled`, matching the sidebar's greyed row.
 */
export function useLedgerSections() {
  const { user, accessToken, myBusinessType } = useSelector((st: { auth: IAuth }) => st.auth);
  const { data: ledgerConfig, isLoading } = useSWR(
    accessToken ? "ledger.config" : null,
    () => LedgerApi.getLedgerConfig(),
    { revalidateOnFocus: false, dedupingInterval: 60000, shouldRetryOnError: false },
  );
  const ledgerOn = !isLoading && ledgerConfig?.mode === "INTERNAL";
  const menus = user?.permissions?.filter((p: { module: string }) => p.module === "INVOICE");

  const visible: VisibleLedgerSection[] = LEDGER_SECTIONS.filter(
    (x) => (!x.supplierOnly || myBusinessType === "SUPPLIER") && (!x.ledgerOnly || ledgerOn),
  ).map((x) => ({ ...x, disabled: !menus?.some((p: { menu: string }) => p.menu === x.menu) }));

  const groups: VisibleLedgerGroup[] = LEDGER_GROUP_ORDER.map((key) => ({
    key,
    sections: visible.filter((x) => x.group === key),
  })).filter((g) => g.sections.length > 0);

  return { ledgerOn, ledgerLoading: isLoading, groups, visible };
}
